import React, { useState, useEffect } from 'react';
import { Building2, CheckCircle2, Lock, AlertCircle, Loader2, FileText } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { useCaprionRejectionGuard } from '@/hooks/useCaprionRejectionGuard';
import CaprionRejectionScreen from './CaprionRejectionScreen';
import KFSModal from './KFSModal';

interface LenderSelectionProps {
  onDone?: () => void;
  isBuilderMode?: boolean;
  registerSubmit?: (submitFn: () => Promise<boolean>) => void;
  // Reports selection + consent up to JourneyPage so the footer Continue button
  // can stay greyed out until a lender is picked AND consent is ticked.
  onChange?: (val: Record<string, any>) => void;
}

interface Lender {
  lender_id: number;
  lender_name: string;
  display_name: string;
  roi_percentage: string;
  min_amount: string;
  max_amount: string;
  tenures: number[];
  display_priority: number;
  is_default: boolean;
  enabled: boolean;
}

interface ApiResponse {
  success: boolean;
  bap?: string;
  program?: string;
  lenders?: Lender[];
  error?: string;
}

const LenderSelection: React.FC<LenderSelectionProps> = ({
  onDone,
  isBuilderMode = false,
  registerSubmit,
  onChange
}) => {
  const [selectedLender, setSelectedLender] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bapName, setBapName] = useState<string>('');
  const [programName, setProgramName] = useState<string>('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [isKFSModalOpen, setIsKFSModalOpen] = useState(false);
  
  // Check rejection status on page load
  const { isRejected, rejectionReason, rejectionTimestamp } = useCaprionRejectionGuard();

  // Fetch available lenders on component mount
  useEffect(() => {
    const fetchLenders = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get<ApiResponse>(
          `${API_BASE_URL}/api/los/get-available-lenders/`,
          { withCredentials: true }
        );

        if (response.data.success && response.data.lenders) {
          setLenders(response.data.lenders);
          setBapName(response.data.bap || '');
          setProgramName(response.data.program || '');

          // Auto-select default lender or first enabled lender
          const defaultLender = response.data.lenders.find(l => l.is_default && l.enabled);
          const firstEnabledLender = response.data.lenders.find(l => l.enabled);
          
          if (defaultLender) {
            setSelectedLender(defaultLender.lender_name.toLowerCase());
          } else if (firstEnabledLender) {
            setSelectedLender(firstEnabledLender.lender_name.toLowerCase());
          }
        } else {
          setError(response.data.error || 'Failed to load lenders');
        }
      } catch (err: any) {
        console.error('Error fetching lenders:', err);
        setError(err.response?.data?.error || 'Failed to load lenders. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLenders();
  }, []);

  const selectedLenderData = lenders.find(l => l.lender_name.toLowerCase() === selectedLender);

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(2)} K`;
    return `₹${num}`;
  };

  const formatTenureRange = (tenures: number[]) => {
    if (!tenures || tenures.length === 0) return 'N/A';
    const min = Math.min(...tenures);
    const max = Math.max(...tenures);
    // For CAPRION (EDI), tenure is in days, not months
    const unit = min > 31 ? 'days' : 'months';
    // Show the full configured range (custom_tenures on BAPProgramLenderConfig);
    // collapse to a single value when only one tenure is configured.
    return min === max ? `${min} ${unit}` : `${min} - ${max} ${unit}`;
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!selectedLender) {
      alert('Please select a lender to continue');
      return false;
    }

    if (!consentGiven) {
      alert('Please provide consent to share your information with the lender');
      return false;
    }

    if (isBuilderMode) {
      console.log('Builder mode: Selected lender:', selectedLender);
      return true;
    }

    setIsSubmitting(true);

    try {
      // Save lender consent
      const consentResponse = await axios.post(
        `${API_BASE_URL}/api/los/save-lender-consent/`,
        {
          lender_name: selectedLender.toUpperCase(),
          consent_given: consentGiven
        },
        { withCredentials: true }
      );

      if (!consentResponse.data.success) {
        alert('Failed to save consent. Please try again.');
        setIsSubmitting(false);
        return false;
      }

      // Create default loan offer (5L, 6 months, EDI)
      const response = await axios.post(
        `${API_BASE_URL}/api/select_lender_and_create_offer/`,
        { lender_name: selectedLender.toUpperCase() },
        { withCredentials: true }
      );

      if (response.data.success) {
        console.log('Default loan offer created:', response.data);
        
        // Store selected lender in session/state
        sessionStorage.setItem('selected_lender', selectedLender);
        
        // Create lead from DB
        console.log('[LENDER SELECTION] Creating Caprion lead...');
        const createLeadResponse = await axios.post(
          `${API_BASE_URL}/api/caprion/create-lead-from-db/`,
          {},
          { withCredentials: true }
        );

        if (!createLeadResponse.data.success) {
          throw new Error(createLeadResponse.data.error || 'Failed to create lead');
        }

        const appId = createLeadResponse.data.application_id;
        console.log('[LENDER SELECTION] ✅ Lead created successfully, application_id:', appId);
        
        // Proceed to next page (Bureau Verification page will handle status polling)
        if (onDone) {
          onDone();
        }
        return true;
        
      } else {
        alert('Failed to create loan offer. Please try again.');
        return false;
      }
    } catch (error) {
      console.error('Error selecting lender:', error);
      alert('Failed to select lender. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Register submit function
  useEffect(() => {
    if (registerSubmit) {
      // If rejected, don't allow submission
      if (isRejected) {
        registerSubmit(async () => false);
      } else {
        registerSubmit(handleSubmit);
      }
    }
  }, [registerSubmit, handleSubmit, isRejected]);

  // Publish gate state to JourneyPage. lender_selected / consent_given are listed in
  // this page's layout.required_fields, so Continue stays grey until both are set.
  useEffect(() => {
    onChange?.({
      lender_selected: selectedLender || '',
      consent_given: consentGiven,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLender, consentGiven]);

  const handleLenderClick = (lender: Lender) => {
    if (lender.enabled) {
      setSelectedLender(lender.lender_name.toLowerCase());
    }
  };

  const handleViewKFS = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent lender card selection when clicking KFS link
    setIsKFSModalOpen(true);
  };

  // Show rejection screen if rejected (from page load check)
  if (isRejected) {
    return (
      <CaprionRejectionScreen
        reason={rejectionReason || undefined}
        timestamp={rejectionTimestamp}
      />
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 className="h-12 w-12 text-intellend-green animate-spin mb-4" />
          <p className="text-gray-600 text-lg">Loading available lenders...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Lenders</h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-intellend-green text-white rounded-lg hover:bg-intellend-green/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No lenders available
  if (lenders.length === 0) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <AlertCircle className="h-12 w-12 text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Lenders Available</h2>
          <p className="text-gray-600 text-center">
            No lenders are currently configured for your program. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Lender Cards */}
      <div className="mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Available Lenders</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {lenders.map((lender) => {
            const isSelected = selectedLender === lender.lender_name.toLowerCase();
            const isDisabled = !lender.enabled;

            return (
              <div
                key={lender.lender_id}
                onClick={() => handleLenderClick(lender)}
                className={`
                  relative rounded-lg border-2 p-4 transition-all duration-200 cursor-pointer
                  ${isSelected
                    ? 'border-intellend-green bg-intellend-green/5 shadow-md'
                    : isDisabled
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : 'border-gray-300 bg-white hover:border-intellend-green hover:shadow-sm'
                  }
                `}
              >
                {/* Selected Indicator */}
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <CheckCircle2 className="h-5 w-5 text-intellend-green" />
                  </div>
                )}

                {/* Disabled Badge */}
                {isDisabled && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">
                    <Lock className="h-3 w-3" />
                    Coming Soon
                  </div>
                )}

                {/* Default Badge */}
                {lender.is_default && lender.enabled && !isSelected && (
                  <div className="absolute top-3 right-3 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                    Recommended
                  </div>
                )}

                <div className="space-y-3">
                  {/* Lender Header */}
                  <div className="flex items-center gap-3">
                    {/* Lender Logo/Icon */}
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
                      ${isSelected
                        ? 'bg-intellend-green text-white'
                        : isDisabled
                          ? 'bg-gray-200 text-gray-400'
                          : 'bg-gray-100 text-gray-600'
                      }
                    `}>
                      <Building2 className="h-6 w-6" />
                    </div>

                    {/* Lender Name */}
                    <div className="flex-1">
                      <h3 className={`
                        text-lg font-bold
                        ${isSelected
                          ? 'text-intellend-green'
                          : isDisabled
                            ? 'text-gray-400'
                            : 'text-gray-900'
                        }
                      `}>
                        {lender.display_name}
                      </h3>
                    </div>
                  </div>

                  {/* Loan Details - Line by Line */}
                  <div className={`
                    space-y-1 text-sm pl-1
                    ${isDisabled ? 'text-gray-400' : 'text-gray-600'}
                  `}>
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-gray-500 min-w-[90px] shrink-0">Loan Range</span>
                      <span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                        {formatCurrency(lender.min_amount)} - {formatCurrency(lender.max_amount)}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-gray-500 min-w-[90px]">Tenure</span>
                      <span className="font-semibold whitespace-nowrap">{formatTenureRange(lender.tenures)}</span>
                    </div>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-gray-500 min-w-[90px]">ROI</span>
                      <span className="font-semibold whitespace-nowrap">{lender.roi_percentage}% p.a.</span>
                    </div>
                  </div>

                  {/* View KFS Link - Always show for enabled lenders */}
                  {lender.enabled && !isDisabled && (
                    <div className="pt-2 border-t border-gray-200">
                      <button
                        onClick={handleViewKFS}
                        className="flex items-center gap-2 text-sm text-intellend-green hover:text-intellend-green/80 font-medium transition-colors"
                      >
                        <FileText className="h-4 w-4" />
                        View Key Fact Statement
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Consent Checkbox */}
      <div className="mt-6 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
              className="mt-1 h-4 w-4 text-intellend-green border-gray-300 rounded focus:ring-intellend-green"
            />
            <span className="ml-3 text-sm text-gray-700">
              I choose to share my information with this lender and consent to the processing of my data for loan application purposes.
            </span>
          </label>
        </div>
      </div>

      {/* Builder Mode Indicator */}
      {isBuilderMode && (
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>🎨 Builder Preview Mode - Select a lender and click Continue in the journey</p>
        </div>
      )}

      {/* KFS Modal */}
      <KFSModal isOpen={isKFSModalOpen} onClose={() => setIsKFSModalOpen(false)} />
    </div>
  );
};

export default LenderSelection;
